// The headline families: news language mapped to the underlyings it plausibly moves (oil to
// crude, "rate cut" to the indices). A port of atlas-geo `headline.rs` over `headlines.tsv`, the
// table embedded below verbatim (its rule lines, tabs and all; copy new rules straight from atlas).
// A headline's families are every rule it matches, in rule order: the first is the strongest.
//
// The patterns are JavaScript source matched case-insensitively without the `u` flag, which is
// what atlas's matcher (`pattern.rs`) reimplements: ASCII `\w` and `\b`, `.` stopping at line
// terminators, lookaheads. So here they are plain `RegExp`s with the `i` flag.
//
// From the table's header:
//   The headline families of src/lib/markets/related-markets.ts (`KW`), in its order: the first rule a
//   headline matches names it. One rule per line, four fields split by tabs: the tag, the underlyings
//   the story moves (comma-separated), the asset class it points at (or empty), and the pattern as
//   JavaScript source, matched case-insensitively (see src/pattern.rs). A venue's own name in a
//   pattern is news about it; the tag the UI shows never names a venue.

import type { AssetClass, Family } from "./types.ts";
import { canonical } from "./symbols.ts";

/** tag, underlyings (comma-separated), class or empty, pattern: tab-separated, one rule a line. */
const TABLE = String.raw`
OIL	CL,BZ,XLE	commodities	\b(oil|crude|opec\+?|brent|wti|petroleum|petrol|gasoline|diesel|pump prices|barrel|refiner(y|ies)?|oil pipeline|tanker|spr|strategic petroleum)\b
NAT GAS	NATGAS		\b(natural gas|nat gas|natgas|\blng\b|ttf|henry hub|nord stream|gas pipeline)\b
SHIPPING LANE	CL,BZ		\b(strait of hormuz|red sea|bab[- ]el[- ]mandeb|houthi|suez canal|panama canal)\b
OIL SUPPLY	CL,BZ		\b(opec|saudi aramco|aramco|venezuela oil|iran(ian)? oil|russian (crude|oil)|oil sanctions|output cuts?)\b
NUCLEAR	URNM		\b(uranium|nuclear (plant|reactor|power)s?|enrichment)\b
MIDEAST RISK	CL,BZ,XAU		\b(iran|tehran|israel|gaza|hezbollah|houthi|iraq|libya|venezuela|nigeria|persian gulf|opec)\b(?=.*\b(strike|attack|missile|escalat|invad|bomb|airstrike|drone|retaliat|sanction|seiz|blockad)\w*\b)|\b(strike|attack|missile|escalat|retaliat|bomb|airstrike)\w*\b(?=.*\b(iran|tehran|israel|gaza|hezbollah|houthi|iraq|libya|persian gulf)\b)
GOLD	XAU,PAXG		\b(gold|bullion|xau|central bank buying)\b
SILVER	XAG		\b(silver|xag)\b
COPPER	XCU		\b(copper|codelco|escondida|freeport)\b
PLATINUM	XPT		\b(platinum|xpt)\b
PALLADIUM	XPD		\b(palladium|xpd|norilsk)\b
RISK-OFF	XAU,UVXY		\b(safe[- ]haven|flight to safety|risk[- ]off)\b
SEMIS	TSM,NVDA,ASML,AMD,MU,AVGO,AMAT,QCOM,MRVL,INTC	equities	\b(semiconductor|chipmaker|chip (export|ban|curb|shortage|maker)s?|foundry|fab plant|wafer|lithography|euv)\b
TAIWAN	TSM,NVDA,ASML		\b(taiwan|tsmc|taipei|taiwan strait)\b
NVIDIA	NVDA		\bnvidia\b
ASML	ASML		\b(asml|extreme ultraviolet)\b
AI INFRA	NVDA,AMD,AVGO,CRWV,NBIS,MU		\b(artificial intelligence|\bai\b (boom|bubble|capex|datacenter|data cent)|data ?cent(er|re)s?|gpu (demand|shortage|cluster)s?|hyperscaler)\b
AI LABS	NVDA,MSFT,GOOGL		\bopenai\b|\banthropic\b|\bchatgpt\b|\bgemini\b(?! man)
KOREA TECH	SAMSUNG,SKHYNIX,SMSN		\b(samsung|sk hynix|hynix)\b
INTEL	INTC		\bintel\b(?=.*\b(chip|foundry|semiconductor|processor|cpu|shares|stock|earnings|corp)\b)|\bchipmaker intel\b
ARM	ARM		\barm (holdings|architecture|chip|shares|stock)\b
APPLE	AAPL		\b(iphone|ipad|macbook|app store|tim cook)\b|\bapple (inc|shares|stock|earnings)\b
MICROSOFT	MSFT		\b(microsoft|azure|copilot)\b
ALPHABET	GOOGL		\b(google|alphabet|youtube|deepmind)\b
AMAZON	AMZN		\b(aws|amazon web services|bezos)\b|\bamazon (shares|stock|earnings|prime|\.com)\b
META	META		\b(facebook|instagram|whatsapp|zuckerberg|meta platforms)\b
TESLA	TSLA		\b(tesla|musk|cybertruck|robotaxi)\b
SPACEX	SPCX		\bspacex\b|\bstarship\b|\bstarlink\b
NETFLIX	NFLX		\b(netflix|streaming wars)\b
RETAIL	WMT,COST,HD		\b(walmart|costco|home depot)\b
GLP-1	LLY,NVO		\b(ozempic|wegovy|glp-?1|novo nordisk|eli lilly|zepbound|mounjaro)\b
CHINA TECH	BABA,TENCENT,PDD,XIAOMI		\b(alibaba|tencent|pinduoduo|xiaomi|china tech|shenzhen)\b
PALANTIR	PLTR		\b(palantir)\b
SPACE	RKLB,ASTS		\b(rocket lab|asts|spacemobile|satellite launches?)\b
BITCOIN	BTC	crypto	\b(bitcoin|\bbtc\b|halving|satoshi)\b
ETHEREUM	ETH		\b(ethereum|ether\b|\beth\b|vitalik)\b
SOLANA	SOL		\b(solana|\bsol\b)\b
HYPE	HYPE		\b(hyperliquid|\bhype\b token|hip-?[34])\b
XRP	XRP		\b(ripple|\bxrp\b)\b
DOGE	DOGE		\b(dogecoin|\bdoge\b)\b
CHAINLINK	LINK		\b(chainlink)\b
EXCHANGES	BNB,COIN		\b(binance|\bbnb\b|coinbase|kraken)\b
STABLECOIN	CRCL	crypto	\b(stablecoin|tether|\busdt\b|\busdc\b|circle\b)\b
BTC TREASURY	MSTR		\b(microstrategy|strategy inc|bitcoin treasury)\b
CRYPTO ETF	BTC,ETH,COIN		\b(bitcoin|spot|crypto|ethereum) etfs?\b|\betf (inflow|outflow)s?\b
CRYPTO		crypto	\b(crypto|blockchain|defi|web3|onchain|on-chain)\b
EXPLOIT		crypto	\b(bridge hack|protocol exploit|rug pull|crypto hack|drained)s?\b
CRYPTO REG		crypto	\b(sec (lawsuit|charge|approval)s?|mica|crypto regulation|cftc)\b
RATES	QQQ,XAU	indices	\b(rate (cut|hike|decision)s?|interest rate|federal reserve|\bfomc\b|\bfed\b|powell|central bank|monetary policy)\b
INFLATION	QQQ,XAU		\b(inflation|\bcpi\b|\bppi\b|core pce|disinflation)\b
GROWTH	QQQ,UVXY		\b(recession|jobs report|nonfarm|unemployment|\bgdp\b|soft landing)\b
TRADE	QQQ,TSM		\b(tariff|trade war|export control|sanction|embargo)\w*
INDICES	QQQ,TQQQ,UVXY	indices	\b(nasdaq|s&p ?500|dow jones|wall street|stock market|\bvix\b|volatility spike)\b
RATES/CREDIT	QQQ,XAU		\b(treasury (yield|auction)s?|\d+[- ]year (treasury|note|bond|yield)|bond yields?|yield curve|debt ceiling|credit rating|downgrade)\b
EARNINGS		equities	\b(earnings (season|beat|miss)e?s?|quarterly results|guidance cut|profit warning)\b
IPO		equities	\b(ipo|listing debut|goes public)\b
`;

const CLASSES: readonly AssetClass[] = ["crypto", "equities", "commodities", "fx", "rates", "indices"];

type Rule = { tag: string; underlyings: string[]; class: AssetClass | null; re: RegExp };

function parse(line: string): Rule | null {
  const fields = line.split("\t");
  if (fields.length !== 4) return null;
  const [tag, syms, cls, source] = fields as [string, string, string, string];
  if (!tag) return null;
  const klass = cls === "" ? null : (CLASSES.find((c) => c === cls) ?? undefined);
  if (klass === undefined) return null;
  const underlyings: string[] = [];
  for (const sym of syms.split(",")) {
    if (!sym) continue;
    const canon = canonical(sym);
    if (!underlyings.includes(canon)) underlyings.push(canon);
  }
  return { tag, underlyings, class: klass, re: new RegExp(source, "i") };
}

/** The rules, in table order, compiled once. */
export const RULES: readonly Rule[] = TABLE.split("\n")
  .filter((line) => line.trim() !== "" && !line.startsWith("#"))
  .map(parse)
  .filter((rule): rule is Rule => rule !== null);

/** Every family the text matches, in rule order, underlyings canonical. */
export function familiesIn(text: string): Family[] {
  const out: Family[] = [];
  for (const rule of RULES) {
    if (rule.re.test(text)) out.push({ tag: rule.tag, underlyings: [...rule.underlyings], class: rule.class });
  }
  return out;
}
